import React, { useState, useEffect } from 'react';
import TranscriptDrainControl from './TranscriptDrainControl';
import { Plus, Trash2, Building2, User, Briefcase, Paintbrush, Users, Settings, MessageSquare, Target, X, Image } from 'lucide-react';

export interface BackgroundInfo {
  customerName: string;
  industry: string;
  context: string;
}

export interface Attendee {
  id: string;
  name: string;
  role: string;
  organization: string;
}

interface SessionSetupViewProps {
  sessionId?: string;
  canEditDrain?: boolean;
  backgroundInfo: BackgroundInfo;
  onSaveBackground: (data: BackgroundInfo) => void;
  goals: string[];
  onSaveGoals: (goals: string[]) => void;
  designContext: string;
  onSaveDesignContext: (context: string) => void;
  imageStyle: string;
  onSaveImageStyle: (style: string) => void;
  attendees: Attendee[];
  onAddAttendee: (attendee: Omit<Attendee, 'id'>) => void;
  onRemoveAttendee: (id: string) => void;
  facilitators: Attendee[];
  onAddFacilitator: (facilitator: Omit<Attendee, 'id'>) => void;
  onRemoveFacilitator: (id: string) => void;
  maxResponseWords: number;
  onSaveMaxResponseWords: (value: number) => void;
  intentDetectEnabled: boolean;
  onToggleIntentDetect: (enabled: boolean) => void;
  avatarActionsEnabled: boolean;
  onToggleAvatarActions: (enabled: boolean) => void;
  avatarTaskActionsEnabled: boolean;
  onToggleAvatarTaskActions: (enabled: boolean) => void;
  avatarBubblesEnabled: boolean;
  onToggleAvatarBubbles: (enabled: boolean) => void;
  threadedConversationsEnabled: boolean;
  onToggleThreadedConversations: (enabled: boolean) => void;
  ttsProvider: 'off' | 'elevenlabs';
  onSetTtsProvider: (value: 'off' | 'elevenlabs') => void;
  converseProvider: 'off' | 'elevenlabs';
  onSetConverseProvider: (value: 'off' | 'elevenlabs') => void;
}

function getInitialColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6'];
  return colors[Math.abs(hash) % colors.length];
}

interface PersonListProps {
  people: Attendee[];
  onAdd: (person: Omit<Attendee, 'id'>) => void;
  onRemove: (id: string) => void;
  addLabel: string;
  namePlaceholder?: string;
  rolePlaceholder?: string;
  orgPlaceholder?: string;
}

const PersonList: React.FC<PersonListProps> = ({
  people,
  onAdd,
  onRemove,
  addLabel,
  namePlaceholder = 'Name *',
  rolePlaceholder = 'Role',
  orgPlaceholder = 'Organization',
}) => {
  const [newPerson, setNewPerson] = useState({ name: '', role: '', organization: '' });

  const handleAdd = () => {
    if (!newPerson.name.trim()) return;
    onAdd(newPerson);
    setNewPerson({ name: '', role: '', organization: '' });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newPerson.name.trim()) {
      handleAdd();
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {people.length > 0 && (
        <div className="flex flex-col gap-2">
          {people.map(person => (
            <div
              key={person.id}
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 group"
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                style={{ backgroundColor: getInitialColor(person.name) }}
              >
                {person.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate">{person.name}</div>
                <div className="text-xs text-slate-400 truncate">
                  {[person.role, person.organization].filter(Boolean).join(' · ')}
                </div>
              </div>
              <button
                onClick={() => onRemove(person.id)}
                className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all"
                title="Remove"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
        <h3 className="text-sm font-semibold text-slate-700">{addLabel}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="relative">
            <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={newPerson.name}
              onChange={e => setNewPerson({ ...newPerson, name: e.target.value })}
              onKeyDown={handleKeyDown}
              placeholder={namePlaceholder}
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
            />
          </div>
          <input
            type="text"
            value={newPerson.role}
            onChange={e => setNewPerson({ ...newPerson, role: e.target.value })}
            onKeyDown={handleKeyDown}
            placeholder={rolePlaceholder}
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
          />
          <input
            type="text"
            value={newPerson.organization}
            onChange={e => setNewPerson({ ...newPerson, organization: e.target.value })}
            onKeyDown={handleKeyDown}
            placeholder={orgPlaceholder}
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
          />
        </div>
        <button
          onClick={handleAdd}
          disabled={!newPerson.name.trim()}
          className="self-start flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus size={16} />
          Add
        </button>
      </div>
    </div>
  );
};

type TabId = 'background' | 'design' | 'imagestyle' | 'facilitators' | 'attendees' | 'response';

const tabs: { id: TabId; label: string; sublabel: string; icon: React.ReactNode }[] = [
  {
    id: 'background',
    label: 'Workshop Background',
    sublabel: 'Customer & goals',
    icon: <Building2 size={15} />,
  },
  {
    id: 'design',
    label: 'Design Context',
    sublabel: 'Theme & style guide',
    icon: <Paintbrush size={15} />,
  },
  {
    id: 'imagestyle',
    label: 'Image Style',
    sublabel: 'Visual tone for AI images',
    icon: <Image size={15} />,
  },
  {
    id: 'facilitators',
    label: 'Facilitators',
    sublabel: 'Session leads',
    icon: <Settings size={15} />,
  },
  {
    id: 'attendees',
    label: 'Attendees',
    sublabel: 'Participants',
    icon: <Users size={15} />,
  },
  {
    id: 'response',
    label: 'Inference Settings',
    sublabel: 'Avatar reply limits',
    icon: <MessageSquare size={15} />,
  },
];

const tabIconBg: Record<TabId, string> = {
  background: 'bg-blue-100 text-blue-600',
  design: 'bg-purple-100 text-purple-600',
  imagestyle: 'bg-orange-100 text-orange-600',
  facilitators: 'bg-amber-100 text-amber-600',
  attendees: 'bg-emerald-100 text-emerald-600',
  response: 'bg-rose-100 text-rose-600',
};

const IMAGE_STYLE_PRESETS = [
  { id: 'infographic', label: 'Infographic', description: 'Data-rich layouts with charts, icons and structured information panels' },
  { id: 'comic', label: 'Comic / Cartoon', description: 'Bold outlines, expressive characters, speech bubbles and dynamic composition' },
  { id: 'doodle', label: 'Doodle Art', description: 'Hand-drawn sketchy lines, warm colours, whimsical and approachable feel' },
  { id: 'watercolour', label: 'Watercolour', description: 'Soft washes, gentle gradients and painterly textures' },
  { id: 'isometric', label: 'Isometric', description: 'Clean 3D perspective, flat colours, tech-forward and precise' },
  { id: 'impressionist', label: 'Impressionist', description: 'Loose brushwork, painterly atmosphere, artistic and emotive' },
];

const SessionSetupView: React.FC<SessionSetupViewProps> = ({
  backgroundInfo,
  onSaveBackground,
  goals,
  onSaveGoals,
  designContext,
  onSaveDesignContext,
  imageStyle,
  onSaveImageStyle,
  attendees,
  onAddAttendee,
  onRemoveAttendee,
  facilitators,
  onAddFacilitator,
  onRemoveFacilitator,
  maxResponseWords,
  onSaveMaxResponseWords,
  intentDetectEnabled,
  sessionId,
  canEditDrain = false,
  onToggleIntentDetect,
  avatarActionsEnabled,
  onToggleAvatarActions,
  avatarTaskActionsEnabled,
  onToggleAvatarTaskActions,
  avatarBubblesEnabled,
  onToggleAvatarBubbles,
  threadedConversationsEnabled,
  onToggleThreadedConversations,
  ttsProvider,
  onSetTtsProvider,
  converseProvider,
  onSetConverseProvider,
}) => {
  const [activeTab, setActiveTab] = useState<TabId>('background');
  const [bgForm, setBgForm] = useState<BackgroundInfo>(backgroundInfo);
  const [bgDirty, setBgDirty] = useState(false);
  const [dcForm, setDcForm] = useState<string>(designContext);
  const [dcDirty, setDcDirty] = useState(false);
  const [mwForm, setMwForm] = useState<number>(maxResponseWords);
  const [mwDirty, setMwDirty] = useState(false);
  const [goalsForm, setGoalsForm] = useState<string[]>(goals);
  const [goalsDirty, setGoalsDirty] = useState(false);

  // Image style state — selectedPreset + freeText notes
  const parseImageStyle = (raw: string) => {
    const match = raw.match(/^PRESET:([^|]+)\|NOTES:(.*)$/s);
    if (match) return { preset: match[1], notes: match[2] };
    return { preset: '', notes: raw };
  };
  const [isPreset, setIsPreset] = useState(() => parseImageStyle(imageStyle).preset);
  const [isNotes, setIsNotes] = useState(() => parseImageStyle(imageStyle).notes);
  const [isDirty, setIsDirty] = useState(false);

  const handleSaveImageStyle = () => {
    const composed = isPreset ? `PRESET:${isPreset}|NOTES:${isNotes}` : isNotes;
    onSaveImageStyle(composed);
    setIsDirty(false);
  };

  useEffect(() => {
    setGoalsForm(goals);
    setGoalsDirty(false);
  }, [goals]);

  useEffect(() => {
    setMwForm(maxResponseWords);
    setMwDirty(false);
  }, [maxResponseWords]);

  const handleBgChange = (field: keyof BackgroundInfo, value: string) => {
    setBgForm(prev => ({ ...prev, [field]: value }));
    setBgDirty(true);
  };

  const handleSaveBg = () => {
    onSaveBackground(bgForm);
    setBgDirty(false);
  };

  const handleDcChange = (value: string) => {
    setDcForm(value);
    setDcDirty(true);
  };

  const handleSaveDc = () => {
    onSaveDesignContext(dcForm);
    setDcDirty(false);
  };

  const handleMwChange = (value: number) => {
    setMwForm(value);
    setMwDirty(true);
  };

  const handleSaveMw = () => {
    if (!Number.isInteger(mwForm) || mwForm < 1 || mwForm > 1000) return;
    onSaveMaxResponseWords(mwForm);
    setMwDirty(false);
  };

  const countBadge = (tab: TabId): number | null => {
    if (tab === 'facilitators') return facilitators.length || null;
    if (tab === 'attendees') return attendees.length || null;
    return null;
  };

  return (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="w-full flex flex-col gap-6">

        {/* Header */}
        <header className="flex items-center gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <Settings size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Session Setup</h1>
            <p className="text-slate-500 text-sm font-medium">Configure workshop context, design guidelines, and participants</p>
          </div>
        </header>

        {/* Tabbed card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">

          {/* Tab bar */}
          <div className="flex overflow-x-auto border-b border-slate-200 bg-slate-50">
            {tabs.map(tab => {
              const isActive = activeTab === tab.id;
              const count = countBadge(tab.id);
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-3 px-5 py-3.5 shrink-0 border-b-2 transition-all text-left ${
                    isActive
                      ? 'border-primary bg-white text-navy'
                      : 'border-transparent text-slate-500 hover:text-navy hover:bg-white/60'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isActive ? tabIconBg[tab.id] : 'bg-slate-200 text-slate-500'}`}>
                    {tab.icon}
                  </div>
                  <div className="min-w-0">
                    <span className={`text-sm font-bold block truncate ${isActive ? 'text-navy' : 'text-slate-600'}`}>
                      {tab.label}
                    </span>
                    <span className={`text-xs ${isActive ? 'text-slate-500' : 'text-slate-400'}`}>
                      {tab.sublabel}
                    </span>
                  </div>
                  {count !== null && (
                    <span className={`ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${isActive ? 'bg-primary/10 text-primary' : 'bg-slate-200 text-slate-500'}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="p-6">

            {/* Workshop Background */}
            {activeTab === 'background' && (
              <div className="flex flex-col gap-5">
                <div>
                  <h2 className="text-base font-bold text-slate-800 mb-1">Workshop Background</h2>
                  <p className="text-sm text-slate-500">
                    Provide context about the customer and workshop objectives. This helps avatars give more relevant contributions.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Session Name</label>
                    <div className="relative">
                      <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        value={bgForm.customerName}
                        onChange={e => handleBgChange('customerName', e.target.value)}
                        placeholder="e.g., Claims Triage Discovery"
                        className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Industry / Domain</label>
                    <div className="relative">
                      <Briefcase size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        value={bgForm.industry}
                        onChange={e => handleBgChange('industry', e.target.value)}
                        placeholder="e.g., Local Government, Healthcare"
                        className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Workshop Goal / Context</label>
                  <textarea
                    value={bgForm.context}
                    onChange={e => handleBgChange('context', e.target.value)}
                    placeholder="Describe the customer's current situation, challenges, constraints, or workshop objectives..."
                    rows={5}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-vertical"
                  />
                  <div className="text-xs text-slate-400 mt-1">{bgForm.context.length} characters</div>
                </div>

                {bgDirty && (
                  <button
                    onClick={handleSaveBg}
                    className="self-start px-5 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors"
                  >
                    Save Background
                  </button>
                )}

                {/* Goals */}
                <div className="border-t border-slate-100 pt-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Target size={15} className="text-primary" />
                    <label className="text-sm font-semibold text-slate-700">Workshop Goals</label>
                    {goalsForm.length > 0 && (
                      <span className="text-xs text-slate-400 ml-auto">{goalsForm.length} goal{goalsForm.length !== 1 ? 's' : ''}</span>
                    )}
                  </div>

                  {goalsForm.length === 0 && (
                    <p className="text-sm text-slate-400 italic mb-3">No goals set. Add one below.</p>
                  )}

                  <div className="flex flex-col gap-2">
                    {goalsForm.map((goal, i) => (
                      <div key={`goal-${i}`} className="flex items-center gap-2"> {/* NOSONAR — ordered editable list, position key is intentional */}
                        <input
                          type="text"
                          value={goal}
                          onChange={e => {
                            const next = [...goalsForm];
                            next[i] = e.target.value;
                            setGoalsForm(next);
                            setGoalsDirty(true);
                          }}
                          placeholder={`Goal ${i + 1}`}
                          className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                        />
                        <button
                          onClick={() => {
                            setGoalsForm(goalsForm.filter((_, idx) => idx !== i));
                            setGoalsDirty(true);
                          }}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors shrink-0"
                          title="Remove goal"
                        >
                          <X size={15} />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-3 mt-3">
                    <button
                      onClick={() => {
                        setGoalsForm([...goalsForm, '']);
                        setGoalsDirty(true);
                      }}
                      className="flex items-center gap-1.5 text-sm text-primary hover:text-primary-dark font-medium transition-colors"
                    >
                      <Plus size={15} />
                      Add goal
                    </button>
                    {goalsDirty && (
                      <button
                        onClick={() => {
                          const filtered = goalsForm.filter(g => g.trim() !== '');
                          setGoalsForm(filtered);
                          setGoalsDirty(false);
                          onSaveGoals(filtered);
                        }}
                        className="ml-auto px-4 py-1.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors"
                      >
                        Save Goals
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Design Context */}
            {activeTab === 'design' && (
              <div className="flex flex-col gap-5">
                <div>
                  <h2 className="text-base font-bold text-slate-800 mb-1">Design Context</h2>
                  <p className="text-sm text-slate-500">
                    Describe the design system, theme, and visual guidelines for this session. This is automatically appended to every design task.
                  </p>
                </div>

                <div className="relative">
                  <Paintbrush size={16} className="absolute left-3 top-3 text-slate-400" />
                  <textarea
                    value={dcForm}
                    onChange={e => handleDcChange(e.target.value)}
                    placeholder="e.g. Tailwind CSS, dark navy background (#1a1f36), primary colour #00c7c4, Inter font, rounded-xl cards, shadcn/ui components..."
                    rows={6}
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-vertical"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">{dcForm.length} characters</span>
                  {dcDirty && (
                    <button
                      onClick={handleSaveDc}
                      className="px-5 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors"
                    >
                      Save Design Context
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Image Style */}
            {activeTab === 'imagestyle' && (
              <div className="flex flex-col gap-5">
                <div>
                  <h2 className="text-base font-bold text-slate-800 mb-1">Image Style</h2>
                  <p className="text-sm text-slate-500">
                    Choose a visual tone for AI-generated images. Applied to every image generated in this session.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {IMAGE_STYLE_PRESETS.map(preset => (
                    <button
                      key={preset.id}
                      onClick={() => { setIsPreset(p => p === preset.id ? '' : preset.id); setIsDirty(true); }}
                      className={`text-left p-3 rounded-xl border-2 transition-all ${
                        isPreset === preset.id
                          ? 'border-primary bg-primary/5'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <div className={`text-sm font-semibold mb-0.5 ${isPreset === preset.id ? 'text-primary' : 'text-slate-800'}`}>
                        {preset.label}
                      </div>
                      <div className="text-xs text-slate-500 leading-snug">{preset.description}</div>
                    </button>
                  ))}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Additional style notes</label>
                  <textarea
                    value={isNotes}
                    onChange={e => { setIsNotes(e.target.value); setIsDirty(true); }}
                    placeholder="e.g. warm colour palette, sympathetic tone, UK council branding, include people..."
                    rows={3}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-vertical"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">{isPreset ? `Preset: ${isPreset}` : 'No preset selected'}</span>
                  {isDirty && (
                    <button
                      onClick={handleSaveImageStyle}
                      className="px-5 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors"
                    >
                      Save Image Style
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Facilitators */}
            {activeTab === 'facilitators' && (
              <div className="flex flex-col gap-4">
                <div>
                  <h2 className="text-base font-bold text-slate-800 mb-1">Facilitators</h2>
                  <p className="text-sm text-slate-500">The people leading and facilitating this workshop session.</p>
                </div>
                <PersonList
                  people={facilitators}
                  onAdd={onAddFacilitator}
                  onRemove={onRemoveFacilitator}
                  addLabel="Add Facilitator"
                  rolePlaceholder="Title"
                />
              </div>
            )}

            {/* Attendees */}
            {activeTab === 'attendees' && (
              <div className="flex flex-col gap-4">
                <div>
                  <h2 className="text-base font-bold text-slate-800 mb-1">Attendees</h2>
                  <p className="text-sm text-slate-500">Track who is participating in this workshop session.</p>
                </div>
                <PersonList
                  people={attendees}
                  onAdd={onAddAttendee}
                  onRemove={onRemoveAttendee}
                  addLabel="Add Attendee"
                />
              </div>
            )}

            {/* Inference Settings */}
            {activeTab === 'response' && (
              <div className="flex flex-col gap-5">
                <div>
                  <h2 className="text-base font-bold text-slate-800 mb-1">Inference Settings</h2>
                  <p className="text-sm text-slate-500">
                    Control how avatars respond during this session. These limits are sent to the AI workflow with every contribution request.
                  </p>
                </div>

                <div className="max-w-sm">
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Max Words Per Response</label>
                  <div className="relative">
                    <MessageSquare size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="number"
                      min={1}
                      max={1000}
                      value={mwForm}
                      onChange={e => handleMwChange(parseInt(e.target.value, 10) || 0)}
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5">Default: 100. Range: 1–1000.</p>
                </div>

                <div className="flex items-center justify-between max-w-sm p-3.5 rounded-xl border border-slate-200 bg-slate-50">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Intent Detection</p>
                    <p className="text-xs text-slate-400 mt-0.5">Automatically detect and trigger session tasks from conversation</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onToggleIntentDetect(!intentDetectEnabled)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${intentDetectEnabled ? 'bg-primary' : 'bg-slate-300'}`}
                    role="switch"
                    aria-checked={intentDetectEnabled}
                  >
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${intentDetectEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>

                {sessionId && (
                  <div className="flex items-center justify-between max-w-sm p-3.5 rounded-xl border border-slate-200 bg-slate-50">
                    <div>
                      <p className="text-sm font-semibold text-slate-700">Transcript Drain</p>
                      <p className="text-xs text-slate-400 mt-0.5">Cleans the transcript every few minutes and keeps the session record current</p>
                    </div>
                    <TranscriptDrainControl sessionId={sessionId} canEdit={canEditDrain} />
                  </div>
                )}

                <div className="flex items-center justify-between max-w-sm p-3.5 rounded-xl border border-slate-200 bg-slate-50">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Avatar Recommended Actions</p>
                    <p className="text-xs text-slate-400 mt-0.5">Show hot-action buttons on avatar replies to create or update requirements</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onToggleAvatarActions(!avatarActionsEnabled)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${avatarActionsEnabled ? 'bg-primary' : 'bg-slate-300'}`}
                    role="switch"
                    aria-checked={avatarActionsEnabled}
                  >
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${avatarActionsEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>

                <div className="flex items-center justify-between max-w-sm p-3.5 rounded-xl border border-slate-200 bg-slate-50">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Avatar Task-Triggering Actions</p>
                    <p className="text-xs text-slate-400 mt-0.5">Let avatars propose running a session task (e.g. generating an HTML artefact) — off by default, always requires a confirm step</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onToggleAvatarTaskActions(!avatarTaskActionsEnabled)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${avatarTaskActionsEnabled ? 'bg-primary' : 'bg-slate-300'}`}
                    role="switch"
                    aria-checked={avatarTaskActionsEnabled}
                  >
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${avatarTaskActionsEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>

                <div className="flex items-center justify-between max-w-sm p-3.5 rounded-xl border border-slate-200 bg-slate-50">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Avatar Mind Bubbles</p>
                    <p className="text-xs text-slate-400 mt-0.5">Show ambient thought bubbles on the second screen</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onToggleAvatarBubbles(!avatarBubblesEnabled)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${avatarBubblesEnabled ? 'bg-primary' : 'bg-slate-300'}`}
                    role="switch"
                    aria-checked={avatarBubblesEnabled}
                  >
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${avatarBubblesEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>

                <div className="flex items-center justify-between max-w-sm p-3.5 rounded-xl border border-slate-200 bg-slate-50">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Threaded Conversations</p>
                    <p className="text-xs text-slate-400 mt-0.5">Show a Participants nav with each participant's avatar chat threads</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onToggleThreadedConversations(!threadedConversationsEnabled)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${threadedConversationsEnabled ? 'bg-primary' : 'bg-slate-300'}`}
                    role="switch"
                    aria-checked={threadedConversationsEnabled}
                  >
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${threadedConversationsEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>

                <div className="max-w-sm">
                  <p className="text-sm font-semibold text-slate-700 mb-1">Text to Speech Provider</p>
                  <p className="text-xs text-slate-400 mb-2">Controls which service synthesises avatar speech audio</p>
                  <div className="flex rounded-xl border border-slate-200 overflow-hidden">
                    {(['off', 'elevenlabs'] as const).map(opt => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => onSetTtsProvider(opt)}
                        className={`flex-1 py-2 text-xs font-semibold transition-all capitalize ${ttsProvider === opt ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                      >
                        {opt === 'off' ? 'Off' : 'ElevenLabs'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="max-w-sm">
                  <p className="text-sm font-semibold text-slate-700 mb-1">Converse Voice Agent</p>
                  <p className="text-xs text-slate-400 mb-2">Controls which service powers the device voice conversation button</p>
                  <div className="flex rounded-xl border border-slate-200 overflow-hidden">
                    {(['off', 'elevenlabs'] as const).map(opt => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => onSetConverseProvider(opt)}
                        className={`flex-1 py-2 text-xs font-semibold transition-all capitalize ${converseProvider === opt ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                      >
                        {opt === 'off' ? 'Off' : 'ElevenLabs'}
                      </button>
                    ))}
                  </div>
                </div>

                {mwDirty && (
                  <button
                    onClick={handleSaveMw}
                    disabled={!Number.isInteger(mwForm) || mwForm < 1 || mwForm > 1000}
                    className="self-start px-5 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Save Inference Settings
                  </button>
                )}
              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
};

export default SessionSetupView;
